import { useNotifications } from '@valuecase/ui-components'
import { useCallback, useMemo, useState } from 'react'
import ArchiveSpacesDialog from './ArchiveSpacesDialog'
import UnarchiveSpacesDialog from './UnarchiveSpacesDialog'
import DeleteSpacesDialog from './DeleteSpacesDialog'
import SpaceMultiSelectActionBar from './SpaceMultiSelectActionBar'
import { useBatchArchiveSpaces } from '@/modules/spaces/hooks/useBatchArchiveSpaces'
import { useBatchDeleteSpaces } from '@/modules/spaces/hooks/useBatchDeleteSpaces'
import { ChangeOwnerSpacesDialog } from './ChangeOwnerSpacesDialog'
import { useBatchChangeSpaceOwner } from '@/modules/spaces/hooks/useBatchChangeSpaceOwner'
import { ReadSpacesListDTO } from '@/modules/spaces/hooks/types'

type SpaceWithOwner = ReadSpacesListDTO & {
  ownerName: string
  ownerEmail?: string
  picture?: string
}

interface SpaceBatchActionWrapperProps {
  selectedSpaces: Set<string>
  spaces: SpaceWithOwner[]
  onSelectionChange: (selectedSpaces: Set<string>) => void
}

const SpaceBatchActionWrapper = ({
  selectedSpaces,
  spaces,
  onSelectionChange,
}: SpaceBatchActionWrapperProps) => {
  const { success, error } = useNotifications()
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showChangeOwnerDialog, setShowChangeOwnerDialog] = useState(false)
  const { batchArchiveSpaces, isLoading: isArchiving } = useBatchArchiveSpaces()
  const { batchDeleteSpaces, isLoading: isDeleting } = useBatchDeleteSpaces()
  const { batchChangeSpaceOwner, isLoading: isChangingOwner } = useBatchChangeSpaceOwner()

  const selectedSpacesList = useMemo(() => {
    return spaces.filter((space) => selectedSpaces.has(space.rootNodeId))
  }, [spaces, selectedSpaces])

  const { hasMixedArchivedState, allArchived } = useMemo(() => {
    if (selectedSpacesList.length === 0) return { hasMixedArchivedState: false, allArchived: false }

    const firstSpaceArchived = selectedSpacesList[0].archived
    const hasMixedArchivedState = selectedSpacesList.some(
      (space) => space.archived !== firstSpaceArchived,
    )

    return {
      hasMixedArchivedState,
      allArchived: firstSpaceArchived,
    }
  }, [selectedSpacesList])

  const handleArchiveClick = useCallback(() => {
    if (allArchived) {
      setShowUnarchiveDialog(true)
    } else {
      setShowArchiveDialog(true)
    }
  }, [allArchived])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteDialog(true)
  }, [])

  const handleArchive = useCallback(async () => {
    try {
      const spaceRootNodeIds = selectedSpacesList.map((space) => space.rootNodeId)
      await batchArchiveSpaces(spaceRootNodeIds, true)
      if (showDeleteDialog) {
        setShowDeleteDialog(false)
      } else {
        setShowArchiveDialog(false)
      }
      success(
        `Successfully archived ${
          spaceRootNodeIds.length === 1 ? 'space' : `${spaceRootNodeIds.length} spaces`
        }`,
      )
      onSelectionChange(new Set())
    } catch (err) {
      error('Failed to archive spaces. Please try again.')
    }
  }, [selectedSpacesList, batchArchiveSpaces, showDeleteDialog, success, onSelectionChange, error])

  const handleUnarchive = useCallback(async () => {
    try {
      const spaceRootNodeIds = selectedSpacesList.map((space) => space.rootNodeId)
      await batchArchiveSpaces(spaceRootNodeIds, false)
      setShowUnarchiveDialog(false)
      success(
        `Successfully unarchived ${
          spaceRootNodeIds.length === 1 ? 'space' : `${spaceRootNodeIds.length} spaces`
        }`,
      )
      onSelectionChange(new Set())
    } catch (err) {
      error('Failed to unarchive spaces. Please try again.')
    }
  }, [selectedSpacesList, batchArchiveSpaces, success, onSelectionChange, error])

  const handleDelete = useCallback(async () => {
    try {
      const spaceRootNodeIds = selectedSpacesList.map((space) => space.rootNodeId)
      await batchDeleteSpaces(spaceRootNodeIds)
      setShowDeleteDialog(false)
      success(
        `Successfully deleted ${
          spaceRootNodeIds.length === 1 ? 'space' : `${spaceRootNodeIds.length} spaces`
        }`,
      )
      onSelectionChange(new Set())
    } catch (err) {
      error('Failed to delete spaces. Please try again.')
    }
  }, [selectedSpacesList, batchDeleteSpaces, success, error, onSelectionChange])

  const handleChangeOwnerClick = useCallback(() => {
    setShowChangeOwnerDialog(true)
  }, [])

  const handleOwnerChange = useCallback(
    async (ownerId: string) => {
      try {
        const spaceRootNodeIds = selectedSpacesList.map((space) => space.rootNodeId)
        await batchChangeSpaceOwner(spaceRootNodeIds, ownerId)
        setShowChangeOwnerDialog(false)
        success(`Successfully changed owner for ${spaceRootNodeIds.length} spaces`)
        onSelectionChange(new Set())
      } catch (err) {
        error('Failed to change space owners. Please try again.')
      }
    },
    [selectedSpacesList, batchChangeSpaceOwner, success, error, onSelectionChange],
  )

  const handleCounterClick = useCallback(() => {
    onSelectionChange(new Set())
  }, [onSelectionChange])

  const hideActionBar = useMemo(() => {
    return showArchiveDialog || showUnarchiveDialog || showDeleteDialog || showChangeOwnerDialog
  }, [showArchiveDialog, showUnarchiveDialog, showDeleteDialog, showChangeOwnerDialog])

  return (
    <>
      {!hideActionBar && (
        <SpaceMultiSelectActionBar
          selectedCount={selectedSpaces.size}
          onCounterClick={handleCounterClick}
          onArchiveClick={handleArchiveClick}
          onDeleteClick={handleDeleteClick}
          onChangeOwnerClick={handleChangeOwnerClick}
          archiveButtonDisabled={hasMixedArchivedState}
          archiveButtonText={allArchived ? 'Unarchive' : 'Archive'}
        />
      )}

      <ArchiveSpacesDialog
        isOpen={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        selectedSpaces={selectedSpacesList}
        onArchive={handleArchive}
        isLoading={isArchiving}
      />

      <UnarchiveSpacesDialog
        isOpen={showUnarchiveDialog}
        onOpenChange={setShowUnarchiveDialog}
        selectedSpaces={selectedSpacesList}
        onUnarchive={handleUnarchive}
        isLoading={isArchiving}
      />

      <DeleteSpacesDialog
        isOpen={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        selectedSpaces={selectedSpacesList}
        onDelete={handleDelete}
        isDeleting={isDeleting}
        isArchiving={isArchiving}
        onArchive={handleArchive}
      />

      <ChangeOwnerSpacesDialog
        isOpen={showChangeOwnerDialog}
        onOpenChange={setShowChangeOwnerDialog}
        selectedSpaces={selectedSpacesList}
        onOwnerChange={handleOwnerChange}
        isLoading={isChangingOwner}
      />
    </>
  )
}

export default SpaceBatchActionWrapper
